import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const ResetPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [isResetSuccess, setIsResetSuccess] = useState(false);

  const navigate = useNavigate();

  // Request password reset
  const handleResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const response = await fetch('https://academichub-backend.onrender.com/auth/reset/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setIsModalOpen(true);
        console.log('Verification code sent successfully to your email.');
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to send reset code. Please try again.');
      }
    } catch (error) {
      console.error('Error requesting password reset:', error);
      setError('An error occurred. Please try again later.');
    }
  };

  // Handle code input
  const handleCodeChange = (index: number, value: string) => {
    if (/^\d?$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      // Auto-focus next input if a digit is entered
      if (value && index < 5) {
        const nextInput = document.getElementById(`code-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  // Reset password with code
  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerificationError('');

    if (newPassword !== confirmPassword) {
      setVerificationError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setVerificationError('Password must be at least 6 characters long');
      return;
    }

    try {
      const verificationCode = code.join('');
      const response = await fetch('https://academichub-backend.onrender.com/auth/reset/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          code: verificationCode,
          newPassword,
        }),
      });

      if (response.ok) {
        setIsModalOpen(false);
        setIsResetSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        const data = await response.json();
        setVerificationError(data.error || 'Failed to reset password. Please try again.');
      }
    } catch (error) {
      console.error('Error during password reset:', error);
      setVerificationError('An error occurred. Please try again later.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center w-full">
      <div className="p-8 rounded-lg shadow-lg w-full max-w-md">
        <div className="flex justify-center">
          <img src="/assets/logo.svg" alt="Logo" className="w-52 h-auto" />
        </div>
        <h1 className="text-4xl font-semibold text-center text-white mb-6">Reset Password</h1>

        {isResetSuccess ? (
          <div className="text-green-500 p-4 rounded-md">
            <p className="text-center">Password reset successful! Redirecting to login...</p>
          </div>
        ) : !isModalOpen ? (
          <>
            {error && <div className="text-red-500 p-3 rounded-md mb-4">{error}</div>}
            <form onSubmit={handleResetRequest} className="space-y-6">
              <p className="text-center text-gray-200 mb-4">
                Enter your email address and we&apos;ll send you a verification code to reset your
                password.
              </p>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full bg-teal-500 text-white py-2 px-4 rounded-md hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-800"
              >
                Send Reset Code
              </button>
            </form>
          </>
        ) : null}

        {/* Verification Code Modal - Same style as Login.tsx */}
        {isModalOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-sm">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Verification Code</h2>
              <p className="text-gray-600 mb-4">
                Enter the 6-digit code we emailed to your institution email.
              </p>
              {verificationError && (
                <div className="text-red-500 p-3 rounded-md mb-4">{verificationError}</div>
              )}
              <form onSubmit={handlePasswordReset} className="space-y-6">
                <div className="flex justify-between mb-4">
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      id={`code-${index}`}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleCodeChange(index, e.target.value)}
                      className="w-10 h-10 text-center text-lg bg-gray-200 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required
                    />
                  ))}
                </div>

                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-teal-500 text-white py-2 px-4 rounded-md hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-800"
                >
                  Reset Password
                </button>
              </form>
            </div>
          </div>
        )}

        <p className="text-center text-gray-400 mt-6">
          <button onClick={() => navigate('/login')} className="text-teal-400 hover:underline">
            Back to Login
          </button>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
